import { useSelector } from "react-redux";

import { Button } from "@seneca/senekit";

import "common/assets/fonts/font-awesome-4.7.0/scss/font-awesome.scss";

import useEffectOnMount from "seneca-common/utils/hooks/useEffectOnMount";

import {
  analyticsLogNewVersionAvailableToastShown,
  analyticsLogToastClosed,
  analyticsLogUserClickedCancelOnToast,
  analyticsLogUserClickedCTAOnToast
} from "features/app/pwa/analytics";
import { getAppVersionIsUpdating } from "features/app/state";
import { getAppName } from "features/plurall";

import CloseButton from "../CloseButton";
import { ToastType } from "../consts";
import { showToast } from "../toast";
import ToastLayout from "../ToastLayout";

export const UPDATE_TOAST_ID = "pwa-update-toast"; // non-forceful
export const FORCEFUL_UPDATE_TOAST_ID = "pwa-warning-toast";

type Props = {
  onUpdateClick: () => void;
  showForcefulUpdatePrompt?: boolean;
  onCloseClick?: () => void;
};

function UpdateAvailableToast({
  updateType,
  showForcefulUpdatePrompt,
  onUpdateClick
}: Props & { updateType: string }) {
  const appVersionIsUpdating = useSelector(getAppVersionIsUpdating);

  useEffectOnMount(() => analyticsLogNewVersionAvailableToastShown(updateType));

  return (
    <ToastLayout
      type={showForcefulUpdatePrompt ? ToastType.warning : ToastType.neutral}
    >
      {{
        title: "Update available",
        description: showForcefulUpdatePrompt ? (
          <>
            We've found an issue with the version of {getAppName()} you are
            using. It will only take a second!
          </>
        ) : (
          <>
            There is a <b>new version</b> of {getAppName()} available. It will
            only take a second!
          </>
        ),
        button: (
          <Button
            loading={appVersionIsUpdating}
            width="fit-content"
            onClick={() => {
              analyticsLogUserClickedCTAOnToast(updateType);
              onUpdateClick();
            }}
          >
            Update
          </Button>
        )
      }}
    </ToastLayout>
  );
}

export function showUpdateAvailableToast(props: Props) {
  const updateType = props.showForcefulUpdatePrompt
    ? "forceful-update"
    : "regular";

  const handleCloseClick = (toastType: "regular" | "forceful-update") => {
    analyticsLogUserClickedCancelOnToast(toastType);
    props.onCloseClick && props.onCloseClick();
  };

  return showToast(
    UpdateAvailableToast,
    {
      autoClose: false,
      onClose: () => analyticsLogToastClosed(updateType),
      toastId: props.showForcefulUpdatePrompt
        ? FORCEFUL_UPDATE_TOAST_ID
        : UPDATE_TOAST_ID,
      closeButton: (
        <CloseButton onCloseClick={() => handleCloseClick(updateType)} />
      )
    },
    { ...props, updateType }
  );
}
